import "@/App.css";
import { createBrowserRouter, RouterProvider, Link } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { ProtectedRoute, AdminRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import TrackOrder from "@/pages/TrackOrder";
import MyOrders from "@/pages/MyOrders";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQs from "@/pages/FAQs";
import { Privacy, Terms } from "@/pages/Legal";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminDelivery from "@/pages/admin/AdminDelivery";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSellers from "@/pages/admin/AdminSellers";
import { SellerAuthProvider } from "@/context/SellerAuthContext";
import { SellerRoute } from "@/components/SellerRoute";
import SellerLogin from "@/pages/seller/SellerLogin";
import SellerRegister from "@/pages/seller/SellerRegister";
import SellerForgotPassword from "@/pages/seller/SellerForgotPassword";
import SellerPending from "@/pages/seller/SellerPending";
import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerProducts from "@/pages/seller/SellerProducts";
import SellerOrders from "@/pages/seller/SellerOrders";
import SellerProfile from "@/pages/seller/SellerProfile";

function NotFound() {
  return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center">
      <p className="font-heading text-6xl font-black text-brand">404</p>
      <h1 className="mt-4 font-heading text-2xl text-ink">Page not found</h1>
      <p className="mt-2 text-ink-soft">The page you're looking for doesn't exist.</p>
      <Link to="/"><Button className="mt-6 rounded-full bg-brand text-ink">Back home</Button></Link>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/products", element: <Products /> },
      { path: "/products/:id", element: <ProductDetail /> },
      { path: "/cart", element: <Cart /> },
      { path: "/checkout", element: <ProtectedRoute><Checkout /></ProtectedRoute> },
      { path: "/track", element: <TrackOrder /> },
      { path: "/my-orders", element: <ProtectedRoute><MyOrders /></ProtectedRoute> },
      { path: "/login", element: <Login /> },
      { path: "/register", element: <Register /> },
      { path: "/about", element: <About /> },
      { path: "/contact", element: <Contact /> },
      { path: "/faqs", element: <FAQs /> },
      { path: "/privacy", element: <Privacy /> },
      { path: "/terms", element: <Terms /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  { path: "/admin/login", element: <AdminLogin /> },
  {
    path: "/admin",
    element: <AdminRoute><AdminLayout /></AdminRoute>,
    children: [
      { index: true, element: <Dashboard /> },
      { path: "products", element: <AdminProducts /> },
      { path: "categories", element: <AdminCategories /> },
      { path: "orders", element: <AdminOrders /> },
      { path: "customers", element: <AdminCustomers /> },
      { path: "sellers", element: <AdminSellers /> },
      { path: "coupons", element: <AdminCoupons /> },
      { path: "delivery", element: <AdminDelivery /> },
      { path: "gallery", element: <AdminGallery /> },
      { path: "settings", element: <AdminSettings /> },
    ],
  },
  { path: "/seller/login", element: <SellerLogin /> },
  { path: "/seller/register", element: <SellerRegister /> },
  { path: "/seller/forgot-password", element: <SellerForgotPassword /> },
  { path: "/seller/pending", element: <SellerPending /> },
  {
    path: "/seller/dashboard",
    element: <SellerRoute><SellerLayout /></SellerRoute>,
    children: [
      { index: true, element: <SellerDashboard /> },
      { path: "products", element: <SellerProducts /> },
      { path: "orders", element: <SellerOrders /> },
      { path: "profile", element: <SellerProfile /> },
    ],
  },
]);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SellerAuthProvider>
          <CartProvider>
            <RouterProvider router={router} />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </SellerAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
